
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Refund & Cancellation Policy</h1>
            
            <div className="text-gray-600 space-y-8">
              <div>
                <p className="text-sm text-gray-500 mb-4">Last updated: January 2024</p>
                <p className="text-lg mb-4">
                  At Baainearme (BNM), we strive to provide excellent service experiences. This policy outlines our refund and cancellation procedures for both customers and partners.
                </p>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Customer Cancellations</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Free Cancellation Window</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Free cancellation up to 2 hours before scheduled service</li>
                    <li>Full refund of advance payment within 5-7 business days</li>
                    <li>No questions asked for cancellations within this window</li>
                    <li>Instant cancellation available through the customer app</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Short Notice Cancellations</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Cancellations within 2 hours incur 25% cancellation fee</li>
                    <li>Remaining 75% refunded to original payment method</li>
                    <li>Fee compensates partner for time and travel preparation</li>
                    <li>Emergency cancellations reviewed case-by-case</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">No-Show Policy</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Customer unavailable at scheduled time = no-show</li>
                    <li>Full advance payment forfeited</li>
                    <li>Partner compensated for time and travel</li>
                    <li>Repeated no-shows may result in account restrictions</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Service Quality Issues</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Unsatisfactory Service</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Report issues within 24 hours of service completion</li>
                    <li>Photo evidence may be required for quality claims</li>
                    <li>Investigation conducted with both customer and partner</li>
                    <li>Resolution options: re-service, partial refund, or full refund</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Partner No-Show</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Partner fails to arrive within 30 minutes of scheduled time</li>
                    <li>Full refund of all payments made</li>
                    <li>Priority rebooking with alternative partner</li>
                    <li>Compensation credit for inconvenience caused</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Incomplete Service</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Partial refund based on work completed</li>
                    <li>Option to have another partner complete remaining work</li>
                    <li>No additional charges for completion service</li>
                    <li>Customer satisfaction is our priority</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Partner Cancellations</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Advance Notice Cancellations</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Partners can cancel up to 2 hours before service</li>
                    <li>No penalty for legitimate cancellations (illness, emergency)</li>
                    <li>Customer automatically notified and offered alternatives</li>
                    <li>Alternative partner assigned when possible</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Last-Minute Cancellations</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Cancellations within 2 hours result in partner penalties</li>
                    <li>Compensation paid to customer for inconvenience</li>
                    <li>Repeated cancellations affect partner ratings</li>
                    <li>May result in temporary account suspension</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Refund Processing</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Processing Timeline</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Digital wallet refunds: Instant to 24 hours</li>
                    <li>Credit/Debit card refunds: 5-7 business days</li>
                    <li>Bank transfer refunds: 7-10 business days</li>
                    <li>Refund status tracking available in customer app</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Refund Methods</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Refunds processed to original payment method</li>
                    <li>BNM wallet credits for faster processing</li>
                    <li>Bank transfer for large refund amounts</li>
                    <li>Alternative methods available upon request</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Special Circumstances</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Weather-Related Cancellations</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Severe weather conditions allow free cancellation</li>
                    <li>Safety of customers and partners is paramount</li>
                    <li>Automatic rescheduling offered when conditions improve</li>
                    <li>Full refund available if rescheduling not possible</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Health and Safety</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Health-related cancellations treated with flexibility</li>
                    <li>COVID-19 symptoms allow penalty-free cancellation</li>
                    <li>Medical certificate may be required for verification</li>
                    <li>Public health guidelines supersede standard policies</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Dispute Resolution Process</h2>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Step 1: Initial Contact</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Contact customer support within 24 hours of issue</li>
                    <li>Provide detailed description and evidence</li>
                    <li>Include booking reference and service details</li>
                    <li>Upload photos or videos if relevant</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Step 2: Investigation</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Support team reviews all evidence</li>
                    <li>Both parties contacted for their version</li>
                    <li>Investigation completed within 48-72 hours</li>
                    <li>Fair and impartial assessment process</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-800">Step 3: Resolution</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Decision communicated to all parties</li>
                    <li>Refund processed if warranted</li>
                    <li>Re-service arranged when appropriate</li>
                    <li>Compensation provided for significant inconvenience</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Non-Refundable Situations</h2>
                <div className="space-y-4">
                  <p>Refunds may not be provided in the following cases:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Service completed satisfactorily as per agreement</li>
                    <li>Customer satisfaction issues based on personal preference</li>
                    <li>Cancellations due to customer's change of mind after service starts</li>
                    <li>Issues arising from customer's failure to provide accurate information</li>
                    <li>Services cancelled due to customer's abusive behavior</li>
                    <li>Force majeure events beyond BNM's control</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Goodwill Gestures</h2>
                <div className="space-y-4">
                  <p>Even in non-refundable situations, we may offer:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Service credits for future bookings</li>
                    <li>Discounts on next service as goodwill</li>
                    <li>Free additional services to address concerns</li>
                    <li>Complimentary service quality improvements</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Contact for Refunds</h2>
                <p className="mb-4">To request a refund or report service issues:</p>
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="space-y-2">
                    <p><strong>Email:</strong> refunds@baainearme.com</p>
                    <p><strong>Phone:</strong> 93074 19845</p>
                    <p><strong>In-App Support:</strong> Available 24/7</p>
                    <p><strong>WhatsApp:</strong> 93074 19845</p>
                    <p><strong>Response Time:</strong> Within 2 hours during business hours</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Policy Updates</h2>
                <p>
                  This refund and cancellation policy may be updated periodically to improve customer experience and operational efficiency. Significant changes will be communicated to all users through email notifications and app updates. The updated policy applies to all bookings made after the effective date.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 420 160"
  className="h-10 text-white"   // change to text-purple-600 etc.
  role="img"
  aria-label="BNM · Baainearme"
>
  <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
    {/* Left ring */}
    <circle cx="50" cy="60" r="40" />
    {/* Vertical divider */}
    <line x1="110" y1="20" x2="110" y2="120" />
  </g>

  {/* Text block */}
  <g fill="currentColor">
    {/* BNM */}
    <text
      x="140"
      y="72"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="800"
      fontSize="56"
    >
      BNM
    </text>

    {/* Baainearme */}
    <text
      x="140"
      y="120"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="700"
      fontSize="32"
    >
      Baainearme
    </text>
  </g>
</svg>

              <p className="text-gray-400">
                Your trusted platform for on-demand home services
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Policies</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer-privacy" className="hover:text-white cursor-pointer">Customer Privacy</Link></li>
                <li><Link to="/partner-privacy" className="hover:text-white cursor-pointer">Partner Privacy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer" className="hover:text-white cursor-pointer">For Customers</Link></li>
                <li><Link to="/partner" className="hover:text-white cursor-pointer">For Partners</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>📞 93074 19845</li>
                <li>✉ support@baainearme.com</li>
                <li><a href="#" className="hover:text-white cursor-pointer">Help Center</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
